/**
 * Wire contract for the in-grid AI assist: natural-language commands that
 * rearrange a data grid's presentation state — and nothing else. The grid
 * context is the model's entire world ("stay in the bounds of what is shown"):
 * columns, current arrangement, saved views, and loaded row count. Dataset
 * narrowing/re-querying is deliberately out of scope (that's the surface
 * assist's job).
 *
 * Shared by the BFF grid workflow (prompt + validation) and MegaGrid's assist
 * integration (context building + plan application). Runtime-light: types and
 * constants only, importable by both builds.
 */

export type GridAssistColumnType = 'text' | 'enum' | 'number' | 'percent' | 'date' | 'boolean';

export interface GridAssistColumn {
    key: string;
    label: string;
    type: GridAssistColumnType;
    hidden: boolean;
    /** Example values for enum-ish columns (explicit options or distincts from loaded rows), capped small. */
    sampleValues?: string[];
}

export type GridAssistSortDir = 'asc' | 'desc';

export interface GridAssistSort {
    key: string;
    dir: GridAssistSortDir;
}

export interface GridAssistGroupBy {
    key: string;
    dir?: GridAssistSortDir;
    mode?: 'count' | 'value';
}

/** Filter operators the assist may emit — matches the grid's own op vocabulary. */
export const GRID_ASSIST_FILTER_OPS = [
    'contains',
    'notContains',
    'equals',
    'notEquals',
    'startsWith',
    'endsWith',
    'anyOf',
    'noneOf',
    'eq',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'dateOn',
    'before',
    'after',
    'dateBetween',
    'blank',
    'notBlank',
    'isTrue',
    'isFalse',
] as const;

export type GridAssistFilterOp = (typeof GRID_ASSIST_FILTER_OPS)[number];

export interface GridAssistFilter {
    key: string;
    op: GridAssistFilterOp;
    value?: string | number;
    /** Upper bound for `between` / `dateBetween`. */
    value2?: string | number;
    /** Values for `anyOf` / `noneOf`. */
    values?: string[];
}

export interface GridAssistView {
    id: string;
    name: string;
    active: boolean;
}

/** Snapshot of what the grid is currently showing — sent with every request. */
export interface GridAssistContext {
    /** Host-supplied description of the dataset, e.g. "AI interaction turns". */
    title?: string;
    columns: GridAssistColumn[];
    sorts: GridAssistSort[];
    groupBy: GridAssistGroupBy[];
    filters: GridAssistFilter[];
    search: string;
    density: 'comfortable' | 'compact';
    rowCount: number;
    /** The user's saved views, so the model can apply/manage them by id. */
    views: GridAssistView[];
}

export interface GridAssistRequest {
    prompt: string;
    grid: GridAssistContext;
}

/**
 * Presentation-state and saved-view operations only. Absent fields mean
 * "leave unchanged"; present arrays REPLACE that aspect wholesale. Application
 * order: reset → applyViewId → state mutations → saveViewAs/renameView/delete.
 */
export interface GridAssistPlan {
    narrative: string;
    /** Reset to the grid's default view before applying anything else. */
    reset?: boolean;
    /** Switch to a saved view (id from context.views) before further mutations. */
    applyViewId?: string;
    /** Absolute column selection: exactly these visible, in this order. */
    visibleColumns?: string[];
    /** Relative: unhide these columns. */
    showColumns?: string[];
    /** Relative: hide these columns. */
    hideColumns?: string[];
    sorts?: GridAssistSort[];
    groupBy?: GridAssistGroupBy[];
    clearGroupBy?: boolean;
    filters?: GridAssistFilter[];
    clearFilters?: boolean;
    search?: string;
    density?: 'comfortable' | 'compact';
    /** Save the RESULTING state (after this plan's mutations) as a named view. */
    saveViewAs?: string;
    renameView?: { id: string; name: string };
    deleteViewId?: string;
}

/** The assist brain, injected into MegaGrid by the host (design stays network-free). */
export type GridAssistHandler = (request: GridAssistRequest) => Promise<GridAssistPlan>;

export const GRID_VISUAL_KINDS = ['ranked-bars', 'donut', 'trend', 'kpi'] as const;
export type GridVisualKind = (typeof GRID_VISUAL_KINDS)[number];

export const GRID_VISUAL_MEASURES = ['turns', 'totalTokens', 'outputTokens'] as const;
export type GridVisualMeasure = (typeof GRID_VISUAL_MEASURES)[number];

/**
 * One LLM-chosen visualization over the grid's current contents. The client
 * renders it deterministically from the visible rows — the model only decides
 * WHAT to show (kind, dimension, measure, title), never the numbers.
 */
export interface GridVisualSpec {
    kind: GridVisualKind;
    title: string;
    /** Column key to bucket by — required for ranked-bars/donut, ignored for trend/kpi. */
    dimension?: string;
    measure: GridVisualMeasure;
    /** Max buckets before the remainder rolls into "Other" (ranked-bars/donut). */
    limit?: number;
}

export interface GridVisualsPlan {
    /** One short line on why these visuals fit the current grid state. */
    narrative?: string;
    visuals: GridVisualSpec[];
}

export interface GridVisualsRequest {
    grid: GridAssistContext;
}

/** The visuals brain: given the grid's state, decides which charts to render. */
export type GridVisualsHandler = (request: GridVisualsRequest) => Promise<GridVisualsPlan>;
